// eslint-disable-next-line import/no-extraneous-dependencies
import { AwsStageType } from "@codedrifters/utils";
import {
  CdkBootstrapOptions,
  CdkDeployOptions,
  CdkDestroyOptions,
  CdkDiffOptions,
  CdkSynthOptions,
  CdkWatchOptions,
} from "./cdk-cli-options";

/*******************************************************************************
 *
 * AWS CDK CLI Defaults
 *
 * Per-stage default option sets the `CdkCli` component layers in when a
 * consumer asks for the options to use against a given deployment target.
 * Phase 1 registered defaults for `cdk deploy`; Phase 2 registers defaults for
 * `synth`, `watch`, `destroy`, `diff`, and `bootstrap`.
 *
 ******************************************************************************/

/**
 * Per-stage defaults for `cdk deploy`.
 *
 * - `dev` opts into `--no-rollback` for fast iteration.
 * - `stage` and `prod` deliberately omit `rollback` so the CLI default
 *   (`true`, i.e. atomic deploys with rollback) applies — this is what allows
 *   replacement-style updates to succeed in stage/prod.
 * - `prod` pins `--method=change-set` to leave a reviewable change-set trail.
 * - `express` is deliberately **not** defaulted on any stage. CloudFormation
 *   express mode skips resource stabilization and flips the CLI's `rollback`
 *   default to `false`, so it is opt-in per consumer via `deployDefaults`,
 *   `accountOverrides`, or a target's `cdkOptions.deploy` — never a built-in.
 */
export const CDK_DEPLOY_DEFAULTS_BY_STAGE: Record<
  AwsStageType,
  Partial<CdkDeployOptions>
> = {
  dev: {
    rollback: false,
    requireApproval: "never",
    progress: "events",
  },
  stage: {
    requireApproval: "never",
    progress: "events",
  },
  prod: {
    requireApproval: "never",
    progress: "events",
    method: "change-set",
  },
};

/**
 * Per-stage defaults for `cdk synth`.
 *
 * Deliberately empty. `--quiet` is what distinguishes the generated `synth`
 * task from `synth:silent`, so it is pinned at the `synth:silent` call site
 * rather than seeded here — a per-stage default would silence both tasks and
 * collapse them into the same command. Consumers that genuinely want a quiet
 * `synth` can still set `synthDefaults[stage].quiet`, which reaches the
 * rendered command; `synth:silent` stays silent regardless.
 */
export const CDK_SYNTH_DEFAULTS_BY_STAGE: Record<
  AwsStageType,
  Partial<CdkSynthOptions>
> = {
  dev: {},
  stage: {},
  prod: {},
};

/**
 * Per-stage defaults for `cdk watch`.
 *
 * Every stage uses `--progress=events` to stream CloudFormation events line by
 * line. The CLI default for `cdk watch --hotswap` is already `true`, so the
 * record leaves it unset.
 */
export const CDK_WATCH_DEFAULTS_BY_STAGE: Record<
  AwsStageType,
  Partial<CdkWatchOptions>
> = {
  dev: { progress: "events" },
  stage: { progress: "events" },
  prod: { progress: "events" },
};

/**
 * Per-stage defaults for `cdk destroy`.
 *
 * `dev` opts into `--force` so local teardown does not prompt; `stage` and
 * `prod` deliberately omit `force` so the CLI's confirmation prompt fires.
 */
export const CDK_DESTROY_DEFAULTS_BY_STAGE: Record<
  AwsStageType,
  Partial<CdkDestroyOptions>
> = {
  dev: { force: true },
  stage: {},
  prod: {},
};

/**
 * Per-stage defaults for `cdk diff`.
 *
 * Empty by default — diff has no flags whose stage-by-stage value is obvious.
 * Reserved for consumer overrides.
 */
export const CDK_DIFF_DEFAULTS_BY_STAGE: Record<
  AwsStageType,
  Partial<CdkDiffOptions>
> = {
  dev: {},
  stage: {},
  prod: {},
};

/**
 * Per-stage defaults for `cdk bootstrap`.
 *
 * `prod` opts into `--termination-protection` so the bootstrap stack cannot be
 * accidentally deleted; `dev` and `stage` omit it.
 */
export const CDK_BOOTSTRAP_DEFAULTS_BY_STAGE: Record<
  AwsStageType,
  Partial<CdkBootstrapOptions>
> = {
  dev: {},
  stage: {},
  prod: { terminationProtection: true },
};
